import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Wykrywanie i wdrażanie nowej wersji aplikacji.
 *
 * Service worker nie przejmuje sterowania sam z siebie — czeka. Ten moduł
 * pilnuje, kiedy nowa wersja jest gotowa, i decyduje, kiedy przełączyć:
 * tuż po starcie robi to sam, a w trakcie pracy pyta, żeby nie zabrać
 * użytkownikowi tego, co właśnie wpisywał.
 */

export type StanAktualizacji = 'brak' | 'gotowa' | 'wdrazanie'

/** Co ile pytać serwer o nową wersję. Kwadrans wystarcza, a nie obciąża łącza. */
const CO_ILE_MS = 15 * 60 * 1000

/**
 * Okno tuż po starcie, w którym aktualizację wdrażamy bez pytania.
 * Użytkownik nic jeszcze nie wpisał, więc nie ma czego przerwać — a aplikacja
 * zainstalowana na ekranie głównym sama dochodzi do najnowszej wersji,
 * bez odinstalowywania i wgrywania od nowa.
 */
const OKNO_STARTU_MS = 6000

/** Gdyby przejęcie sterowania nie doszło do skutku, i tak odświeżamy. */
const AWARYJNE_PRZELADOWANIE_MS = 4000

function przeladujPoPrzejeciu(oczekujacy: ServiceWorker): void {
  let przeladowano = false
  const przeladuj = () => {
    if (przeladowano) return
    przeladowano = true
    window.location.reload()
  }

  navigator.serviceWorker.addEventListener('controllerchange', przeladuj)
  oczekujacy.postMessage({ typ: 'PRZEJMIJ' })
  setTimeout(przeladuj, AWARYJNE_PRZELADOWANIE_MS)
}

export function useAktualizacja(): {
  stan: StanAktualizacji
  zastosuj: () => void
} {
  const [stan, ustawStan] = useState<StanAktualizacji>('brak')
  const czekajacy = useRef<ServiceWorker | null>(null)

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return

    let zywy = true
    let timer: ReturnType<typeof setInterval> | null = null
    const start = Date.now()

    function zglos(rejestracja: ServiceWorkerRegistration) {
      const oczekujacy = rejestracja.waiting
      // Bez `controller` to pierwsza instalacja, a nie aktualizacja —
      // nie ma o czym informować.
      if (oczekujacy === null || navigator.serviceWorker.controller === null) return
      if (!zywy) return

      czekajacy.current = oczekujacy

      if (Date.now() - start < OKNO_STARTU_MS) {
        ustawStan('wdrazanie')
        przeladujPoPrzejeciu(oczekujacy)
        return
      }
      ustawStan('gotowa')
    }

    void navigator.serviceWorker
      .register('/sw.js')
      .then((rejestracja) => {
        if (!zywy) return
        zglos(rejestracja)

        rejestracja.addEventListener('updatefound', () => {
          const nowy = rejestracja.installing
          if (nowy === null) return
          nowy.addEventListener('statechange', () => {
            if (nowy.state === 'installed') zglos(rejestracja)
          })
        })

        const sprawdz = () => {
          void rejestracja.update().catch(() => {
            // Brak sieci — spróbujemy przy następnym przebiegu.
          })
        }
        timer = setInterval(sprawdz, CO_ILE_MS)

        // Powrót do aplikacji to najlepszy moment na sprawdzenie: w wersji
        // zainstalowanej na ekranie głównym to właśnie wtedy wraca ona z tła.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') sprawdz()
        })
      })
      .catch(() => {
        // Rejestracja nie powiodła się — aplikacja działa dalej, tylko bez
        // trybu offline i bez automatycznych aktualizacji.
      })

    return () => {
      zywy = false
      if (timer !== null) clearInterval(timer)
    }
  }, [])

  const zastosuj = useCallback(() => {
    const oczekujacy = czekajacy.current
    if (oczekujacy === null) return
    ustawStan('wdrazanie')
    przeladujPoPrzejeciu(oczekujacy)
  }, [])

  return { stan, zastosuj }
}
